import { Link, useLocation } from 'react-router-dom';
import { Zap, Map, Repeat } from 'lucide-react';
import useUserStore from '../../stores/useUserStore';
import { FOCUS_MODES, modeFromPath } from './focusModes';
import './FocusModeTabs.css';

/** Mode id → icon. Kept here so focusModes.js stays data-only. */
const ICONS = {
    freestyle: Zap,
    circuit: Map,
    rituals: Repeat,
};

/**
 * FocusModeTabs — switches the Focus page between Freestyle, Circuit and
 * Rituals. Mounted in the app chrome (Header on desktop, MobileHeader on
 * mobile), not in the page, so it is reachable from every mode. It used to live
 * inside Freestyle's pre-session block, which meant Circuit mode had no way back
 * except the sidebar.
 *
 * NOT `role="tablist"`. Each mode owns a real URL, so these are navigation
 * links; announcing them as tabs would promise a tabpanel relationship that does
 * not exist. `aria-current="page"` carries selection.
 *
 * NOT `NavLink`. It recomputes `aria-current` from its own pathname match and
 * overwrites what it is handed — the documented reason Sidebar and
 * MobileBottomNav both use plain `Link`. `modeFromPath` is the only authority
 * on selection here.
 *
 * @param {'md'|'sm'} size  'sm' is the compact variant for the mobile header.
 */
export default function FocusModeTabs({ size = 'md' }) {
    const { pathname } = useLocation();
    const isModuleEnabled = useUserStore(s => s.isModuleEnabled);

    const activeMode = modeFromPath(pathname);
    const modes = FOCUS_MODES.filter(m => !m.module || isModuleEnabled(m.module));

    return (
        <nav
            className={`focus-mode-tabs${size === 'sm' ? ' focus-mode-tabs--sm' : ''}`}
            aria-label="Focus mode"
        >
            {modes.map(mode => {
                const Icon = ICONS[mode.id];
                const selected = mode.id === activeMode;
                return (
                    <Link
                        key={mode.id}
                        to={mode.path}
                        className="focus-mode-tabs__link"
                        aria-current={selected ? 'page' : undefined}
                    >
                        <Icon size={size === 'sm' ? 14 : 16} aria-hidden="true" />
                        {mode.label}
                    </Link>
                );
            })}
        </nav>
    );
}
